import { useEffect, useState } from 'react'
import { openParentEvents, type ParentPushEvent } from '../api/parentEvents'

/**
 * The parent push channel, as a list a screen can draw.
 *
 * 🔴 IT IS "WHAT ARRIVED WHILE THIS TAB WAS OPEN", AND NOTHING MORE. A reload
 * clears it, deliberately — persisting would imply an inbox, a durable record of
 * what a parent has and has not seen, and nothing on the server backs that
 * claim. What IS durable is the announcement itself, which
 * `GET /parent/announcements` returns whether or not a frame ever arrived.
 * `useStaffEvents` makes the same choice for the same reason.
 *
 * 🔴 SO A SCREEN MUST MERGE, NEVER REPLACE. The list this returns is a *head* —
 * the newest few — and the fetched list is the record. `mergeAnnouncements`
 * below is where the two meet, and it exists so no screen writes that fold
 * twice.
 */
export function useParentEvents(token: string | undefined, limit = 10): ParentPushEvent[] {
  const [events, setEvents] = useState<ParentPushEvent[]>([])

  useEffect(() => {
    /*
     * ⚠️ NO TOKEN, NO CONNECTION. A socket opened without one is disconnected by
     * the server on the handshake, so attempting it would be a reconnect loop
     * against a door that is never going to open.
     */
    if (token === undefined || token === '') return undefined

    return openParentEvents(token, (event) => {
      setEvents((prev) => {
        /*
         * ⚠️ DEDUPED ON THE ANNOUNCEMENT'S OWN ID. socket.io redelivers on
         * reconnect in some transports, and a parent watching the same notice
         * appear three times learns to distrust the screen. The id is the
         * server's row id, so this is exact rather than heuristic — which is why
         * the frame carries one at all.
         */
        if (prev.some((e) => e.id === event.id)) return prev
        return [event, ...prev].slice(0, limit)
      })
    })
  }, [token, limit])

  return events
}

/**
 * The fetched list, with anything that arrived since folded in.
 *
 * 🔴 THE FETCHED ROW WINS ON A COLLISION, AND THAT IS NOT ARBITRARY. A frame is
 * a copy of what the server wrote; the fetched row IS what the server wrote, and
 * it carries the fields a frame does not (the school's name). A frame for a
 * notice already in the list is therefore nothing new to show.
 *
 * ⚠️ THE MERGED ROWS ARE SHAPED LIKE FETCHED ONES SO THE SCREEN HAS ONE LIST TO
 * DRAW. The two fields a frame cannot supply are named here rather than left
 * undefined — a card whose school name is blank for the first thirty seconds and
 * then fills in is worse than one that says where it came from.
 */
export function mergeAnnouncements<
  T extends {
    id: string
    title: string
    body: string
    authorLabel: string
    createdAt: string
  },
>(fetched: readonly T[], live: readonly ParentPushEvent[], fill: Omit<T, keyof LiveFields>): T[] {
  const known = new Set(fetched.map((a) => a.id))
  const extra = live
    .filter((e) => e.type === 'announcement.posted' && !known.has(e.id))
    .map(
      (e) =>
        ({
          ...fill,
          id: e.id,
          title: e.title,
          body: e.detail ?? '',
          authorLabel: e.from ?? '',
          createdAt: e.at,
        }) as unknown as T,
    )

  /* Newest first, and stable: two notices posted in the same second keep the
     order the server listed them in rather than swapping between renders. */
  return [...extra, ...fetched].sort((a, z) => z.createdAt.localeCompare(a.createdAt))
}

/** The fields a live frame can supply. Everything else comes from `fill`. */
interface LiveFields {
  id: string
  title: string
  body: string
  authorLabel: string
  createdAt: string
}
